import React from 'react';

import { Tooltip, Typography } from 'antd';

import styles from './styles.module.less';

const { Text } = Typography;

export interface MetricCardItem {
  key: string;
  value: React.ReactNode;
  label: string;
  detail: string;
  /** Explains how the metric is counted. */
  helper?: string;
  /** Accessible name for the helper control. */
  helperLabel?: string;
  /** When present the card is a button that runs this action (e.g. opens a drill-down). */
  onClick?: () => void;
  /** Accessible name for the interactive card. */
  actionLabel?: string;
  /** Shown on click when the card has no action, explaining why. */
  unavailableHint?: string;
}

export interface MetricTooltipProps {
  label: string;
  content: string;
  className?: string;
  children: React.ReactNode;
}

/** Shows a metric explanation when its label/value is hovered or focused. */
export const MetricTooltip: React.FC<MetricTooltipProps> = ({ label, content, className, children }) => (
  <Tooltip title={content} trigger={['hover', 'focus']}>
    <button type="button" className={`${styles.tooltipTarget} ${className ?? ''}`} aria-label={label}>
      {children}
    </button>
  </Tooltip>
);

const MetricCard: React.FC<Omit<MetricCardItem, 'key'>> = ({
  value,
  label,
  detail,
  helper,
  helperLabel,
  onClick,
  actionLabel,
  unavailableHint,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  const card = (
    <div
      className={`${styles.metric} ${onClick ? styles.interactive : ''}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? actionLabel : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Text className={styles.value}>{value}</Text>
      <div className={styles.labelRow}>
        {helper ? (
          <MetricTooltip label={helperLabel ?? label} content={helper} className={styles.label}>
            {label}
          </MetricTooltip>
        ) : <Text className={styles.label}>{label}</Text>}
      </div>
      <Text type="secondary" className={styles.detail}>
        {detail}
      </Text>
    </div>
  );

  if (onClick || !unavailableHint) return card;

  return (
    <Tooltip title={unavailableHint} trigger={['click']}>
      {card}
    </Tooltip>
  );
};

export default MetricCard;
